import { type FC, useState, useMemo, useCallback } from 'react';
import type { Node as ReactFlowNode } from 'reactflow';
import type { NodeData } from '@/types/node.types';
import { NodeType, NodeStatus, NODE_TYPE_COLORS, NODE_STATUS_LABELS } from '@/types/node.types';
import { useReactFlow } from 'reactflow';
import { exportTableToXLSX } from '@/services/exportXlsx';
import { useCanvasStore } from '@/stores/canvas.store';

interface TableViewProps {
  nodes: ReactFlowNode<NodeData>[];
  onSelectNode?: (nodeId: string) => void;
}

const NODE_ICONS: Record<string, string> = {
  Vision: '\u2605',
  Objective: '\u25CE',
  Activity: '\u26A1',
  Risk: '\u26A0',
  Opportunity: '\u2728',
  Resource: '\u2699',
  KPI: '\u{1F4CA}',
  Stakeholder: '\u{1F465}',
};

const statusColors: Record<string, string> = {
  Active: '#22C55E',
  Paused: '#F59E0B',
  Archived: '#6B7280',
  ToReview: '#8B5CF6',
};

const NODE_TYPE_LABELS: Record<string, string> = {
  Vision: 'Vision',
  Objective: 'Objectif',
  Activity: 'Activit\u00e9',
  Risk: 'Risque',
  Opportunity: 'Opportunit\u00e9',
  Resource: 'Ressource',
  KPI: 'KPI',
  Stakeholder: 'Partie prenante',
};

type SortField = 'type' | 'title' | 'priority' | 'status' | 'createdAt';
type SortDir = 'asc' | 'desc';

const toNum = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const fmtEuro = (n: number): string => {
  if (Math.abs(n) >= 1000) return `${(Math.round(n / 100) / 10).toLocaleString('fr-FR')} k€`;
  return `${Math.round(n).toLocaleString('fr-FR')} €`;
};

const renderProgressCell = (raw: unknown, color: string): JSX.Element | string => {
  const p = toNum(raw);
  if (p === null) return '—';
  const clamped = Math.max(0, Math.min(100, p));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div
        style={{
          width: 50,
          height: 4,
          borderRadius: 2,
          backgroundColor: 'var(--color-border)',
          overflow: 'hidden',
        }}
      >
        <div style={{ width: `${clamped}%`, height: '100%', backgroundColor: color }} />
      </div>
      <span style={{ fontWeight: 600 }}>{clamped}%</span>
    </div>
  );
};

const renderBudgetCell = (plannedRaw: unknown, spentRaw: unknown): JSX.Element | string => {
  const planned = toNum(plannedRaw);
  const spent = toNum(spentRaw);
  if (planned === null && spent === null) return '—';
  const over = planned !== null && spent !== null && spent > planned;
  return (
    <span style={{ color: over ? '#EF4444' : undefined, fontWeight: over ? 700 : 500 }}>
      {spent !== null ? fmtEuro(spent) : '—'} / {planned !== null ? fmtEuro(planned) : '—'}
    </span>
  );
};

const fmtDays = (n: number): string => {
  const rounded = Math.round(n * 10) / 10;
  return `${rounded.toLocaleString('fr-FR')} j`;
};

const renderEffortCell = (plannedRaw: unknown, spentRaw: unknown): JSX.Element | string => {
  const planned = toNum(plannedRaw);
  const spent = toNum(spentRaw);
  if (planned === null && spent === null) return '—';
  const over = planned !== null && spent !== null && spent > planned;
  return (
    <span style={{ color: over ? '#EF4444' : undefined, fontWeight: over ? 700 : 500 }}>
      {spent !== null ? fmtDays(spent) : '—'} / {planned !== null ? fmtDays(planned) : '—'}
    </span>
  );
};

export const TableView: FC<TableViewProps> = ({ nodes, onSelectNode }) => {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('title');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const reactFlowInstance = useReactFlow();
  const canvasName = useCanvasStore((s) => s.canvas?.name ?? 'canvas');

  const toggleTypeFilter = useCallback((type: string) => {
    setTypeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  const handleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        setSortDir('asc');
      }
    },
    [sortField],
  );

  const filteredNodes = useMemo(() => {
    let result = nodes.filter((n) => n.type !== 'group');

    if (search) {
      const lower = search.toLowerCase();
      result = result.filter((n) => n.data.title.toLowerCase().includes(lower));
    }

    if (typeFilter.size > 0) {
      result = result.filter((n) => typeFilter.has(n.data.type));
    }

    if (statusFilter !== 'all') {
      result = result.filter((n) => n.data.status === statusFilter);
    }

    result.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'type':
          cmp = a.data.type.localeCompare(b.data.type);
          break;
        case 'title':
          cmp = a.data.title.localeCompare(b.data.title);
          break;
        case 'priority':
          cmp = a.data.priority - b.data.priority;
          break;
        case 'status':
          cmp = a.data.status.localeCompare(b.data.status);
          break;
        case 'createdAt':
          cmp = (a.data.createdAt || '').localeCompare(b.data.createdAt || '');
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [nodes, search, typeFilter, statusFilter, sortField, sortDir]);

  const handleRowClick = useCallback(
    (nodeId: string) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;
      if (onSelectNode) onSelectNode(nodeId);
      reactFlowInstance.setCenter(node.position.x + 100, node.position.y + 50, {
        zoom: 1.2,
        duration: 400,
      });
    },
    [nodes, reactFlowInstance, onSelectNode],
  );

  const sortIndicator = (field: SortField) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' \u25B2' : ' \u25BC';
  };

  const thStyle: React.CSSProperties = {
    padding: 'var(--spacing-sm) var(--spacing-md)',
    textAlign: 'left',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    color: 'var(--color-text-muted)',
    borderBottom: '1px solid var(--color-border)',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
  };

  const tdStyle: React.CSSProperties = {
    padding: 'var(--spacing-xs) var(--spacing-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text)',
    borderBottom: '1px solid var(--color-border)',
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: 'var(--color-surface)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Filter bar */}
      <div
        style={{
          padding: 'var(--spacing-md)',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-md)',
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-md)',
            fontWeight: 600,
            color: 'var(--color-text)',
          }}
        >
          Vue tableau
        </span>

        {/* Search */}
        <input
          type="text"
          placeholder="Rechercher par titre..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: 'var(--spacing-xs) var(--spacing-sm)',
            backgroundColor: 'var(--color-background)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-text)',
            fontSize: 'var(--font-size-xs)',
            outline: 'none',
            minWidth: 180,
          }}
        />

        {/* Type filters */}
        <div style={{ display: 'flex', gap: 'var(--spacing-xs)' }}>
          {Object.values(NodeType).map((t) => {
            const active = typeFilter.has(t);
            return (
              <button
                key={t}
                onClick={() => toggleTypeFilter(t)}
                style={{
                  padding: '2px 8px',
                  fontSize: 'var(--font-size-xs)',
                  backgroundColor: active ? `${NODE_TYPE_COLORS[t]}25` : 'transparent',
                  border: `1px solid ${active ? NODE_TYPE_COLORS[t] : 'var(--color-border)'}`,
                  borderRadius: 'var(--radius-sm)',
                  color: active ? NODE_TYPE_COLORS[t] : 'var(--color-text-muted)',
                  cursor: 'pointer',
                  transition: 'var(--transition-fast)',
                }}
              >
                {NODE_ICONS[t]} {NODE_TYPE_LABELS[t]}
              </button>
            );
          })}
        </div>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: 'var(--spacing-xs) var(--spacing-sm)',
            backgroundColor: 'var(--color-background)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-text)',
            fontSize: 'var(--font-size-xs)',
            cursor: 'pointer',
            outline: 'none',
          }}
        >
          <option value="all">Tous les statuts</option>
          {Object.values(NodeStatus).map((s) => (
            <option key={s} value={s}>
              {NODE_STATUS_LABELS[s]}
            </option>
          ))}
        </select>

        {/* Spacer + export */}
        <div style={{ marginLeft: 'auto' }}>
          <button
            onClick={() => exportTableToXLSX(canvasName, filteredNodes)}
            title="Exporter le tableau filtré en XLSX"
            style={{
              padding: 'var(--spacing-xs) var(--spacing-sm)',
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--color-text)',
              cursor: 'pointer',
              fontSize: 'var(--font-size-xs)',
            }}
          >
            Exporter XLSX
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
          }}
        >
          <thead>
            <tr>
              <th onClick={() => handleSort('type')} style={thStyle}>
                Type{sortIndicator('type')}
              </th>
              <th onClick={() => handleSort('title')} style={thStyle}>
                Titre{sortIndicator('title')}
              </th>
              <th onClick={() => handleSort('priority')} style={thStyle}>
                Priorit\u00e9{sortIndicator('priority')}
              </th>
              <th onClick={() => handleSort('status')} style={thStyle}>
                Statut{sortIndicator('status')}
              </th>
              <th style={{ ...thStyle, cursor: 'default' }}>Avancement</th>
              <th style={{ ...thStyle, cursor: 'default' }}>Budget</th>
              <th style={{ ...thStyle, cursor: 'default' }}>Effort (j/h)</th>
              <th style={{ ...thStyle, cursor: 'default' }}>Tags</th>
              <th onClick={() => handleSort('createdAt')} style={thStyle}>
                Date de cr\u00e9ation{sortIndicator('createdAt')}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredNodes.length === 0 ? (
              <tr>
                <td
                  colSpan={9}
                  style={{
                    ...tdStyle,
                    textAlign: 'center',
                    padding: 'var(--spacing-xl)',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  Aucun noeud trouv\u00e9
                </td>
              </tr>
            ) : (
              filteredNodes.map((node) => {
                const d = node.data;
                const color = NODE_TYPE_COLORS[d.type] || '#6B7280';
                return (
                  <tr
                    key={node.id}
                    onClick={() => handleRowClick(node.id)}
                    style={{ cursor: 'pointer', transition: 'var(--transition-fast)' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-background)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <td style={tdStyle}>
                      <span style={{ color, marginRight: 4 }}>{NODE_ICONS[d.type]}</span>
                      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                        {NODE_TYPE_LABELS[d.type]}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{d.title}</td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: 700,
                          color: '#fff',
                          backgroundColor: color,
                          borderRadius: 'var(--radius-sm)',
                          padding: '1px 6px',
                        }}
                      >
                        P{d.priority}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                        <span
                          style={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            backgroundColor: statusColors[d.status] || '#6B7280',
                            display: 'inline-block',
                          }}
                        />
                        <span style={{ fontSize: 'var(--font-size-xs)' }}>
                          {NODE_STATUS_LABELS[d.status]}
                        </span>
                      </div>
                    </td>
                    <td style={{ ...tdStyle, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      {renderProgressCell(d.progress, color)}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      {renderBudgetCell(d.budgetPlanned, d.budgetSpent)}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      {renderEffortCell(d.effortPlannedDays, d.effortSpentDays)}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 'var(--spacing-xs)', flexWrap: 'wrap' }}>
                        {(d.tags || []).map((tag, i) => (
                          <span
                            key={i}
                            style={{
                              fontSize: 'var(--font-size-xs)',
                              padding: '0 5px',
                              borderRadius: 'var(--radius-sm)',
                              backgroundColor: 'var(--color-background)',
                              color: 'var(--color-text-muted)',
                              border: '1px solid var(--color-border)',
                            }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      {d.createdAt ? new Date(d.createdAt).toLocaleDateString('fr-FR') : '\u2014'}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TableView;
